import React, { FC, useEffect, useState } from "react"
import { LCReleaseForm, ReleaseDetailProps, Track, User } from "../../../types"
import "./styles/Catalog.css"
import { connect } from "react-redux"
import { RootState } from "../../../store"
import { useNavigate, useParams } from "react-router-dom"
import trackColumns from "./columns/trackColumns"
import { fetchReleaseTracks, selectRelease, updateReleaseEditStatus } from "../../../actions/catalogActions"
import { hashids } from "../../../utility"
import ReleaseDetailTable from "./release-detail-components/ReleaseDetailTable"
import ReleaseDetailFilterComponents from "./release-detail-components/ReleaseDetailFilterComponent"
import ReleaseComponent from "./ReleaseComponent"
import { lang } from "../../../constants/languages"
import { KSpan } from "kahuna-base-react-components"
import { releases as releasesApi } from "../../../apis"
import Loading from "../../ui/Loading"
import ReleaseLCView from "./label-camp-view/ReleaseLCView"
import { PERMISSIONS } from "../../../constants"

const ReleaseDetail: FC<ReleaseDetailProps> = (props) => {
  const { userId, releases, release, selectRelease, fetchReleaseTracks, paginatedTracks, isAdmin } = props
  const [loading, setLoading] = useState(true)
  const params = useParams()
  const decodedId = hashids.decode(params.releaseId || "").toString()
  const limit = 200
  const [tableData, setTableData] = useState<Track[]>([])
  const [showDetail, setShowDetail] = useState(false)
  const [releaseForm, setReleaseForm] = useState<LCReleaseForm>()
  const [options, setOptions] = useState({})

  useEffect(() => {
    if (params.releaseId) {
      selectRelease(
        releases.find((r) => r.id?.toString() === decodedId),
        decodedId
      )
      const filteredUserId = isAdmin ? 0 : userId
      fetchReleaseTracks(limit, 0, decodedId, filteredUserId).then(() => setLoading(false))
    }

    window.scrollTo(0, 0)
  }, [])

  const fetchProduct = (productId: string) => {
    if (releaseForm) {
      return
    }

    setLoading(true)
    releasesApi(`/release/label_camp_options/`, "get").then((resp) => {
      const labelOptions = resp.data["label_options"]
      const productTypeOptions = resp.data["product_type_options"]
      const languageOptions = resp.data["language_options"]
      const metadataLanguageOptions = resp.data["metadata_language_options"]
      const productGenreOptions = resp.data["product_genre_options"]
      const distributorProductSubGenreOptions = resp.data["distributor_product_sub_genre_options"]
      const distributorPriceCodeOptions = resp.data["distributor_price_code_options"]
      const distributorTrackPriceOptions = resp.data["distributor_track_price_options"]
      const roleOptions = resp.data["roles"]
      setOptions({
        labelOptions,
        productTypeOptions,
        languageOptions,
        metadataLanguageOptions,
        productGenreOptions,
        distributorProductSubGenreOptions,
        distributorPriceCodeOptions,
        distributorTrackPriceOptions,
        roleOptions
      })
      releasesApi(`/release/label_camp_product_get/?product_id=${productId}`, "get").then((resp) => {
        setReleaseForm({ ...resp.data.result })
        setLoading(false)
      })
    })
  }

  const onClickPage = async (pageNumber: number) => {
    setLoading(true)
    const filteredUserId = isAdmin ? 0 : userId
    await fetchReleaseTracks(limit, (pageNumber - 1) * limit, decodedId, filteredUserId)
    setLoading(false)
  }

  const filterDataBySearchText = (searchText: string): void => {
    if (props.paginatedTracks.results.length > 0 && searchText.length > 0) {
      const filteredData = props.paginatedTracks.results.filter((track) => {
        return track.title.toLowerCase().includes(searchText.toLowerCase())
      })
      setTableData(filteredData.filter((track) => !track.archived).sort((a, b) => (b.number < a.number ? 1 : -1)))
    } else {
      setTableData(
        props.paginatedTracks.results.filter((track) => !track.archived).sort((a, b) => (b.number < a.number ? 1 : -1))
      )
    }
  }

  useEffect(() => {
    setTableData(
      props.paginatedTracks.results.filter((track) => !track.archived).sort((a, b) => (b.number < a.number ? 1 : -1))
    )
  }, [props.paginatedTracks.results])

  const onSwitchDetail = () => {
    const showDetailToUpdate = !showDetail
    setShowDetail(showDetailToUpdate)
    if (showDetailToUpdate && release.productId) {
      fetchProduct(release.productId)
    }
  }

  return (
    <div className="release-detail bg-white">
      {loading && <Loading />}
      {release.productId && (
        <div className="flex gap-2 items-center mb-4">
          <div
            onClick={() => onSwitchDetail()}
            style={{ background: showDetail ? "#111" : "#11111120" }}
            className={`w-9 h-5 flex items-center rounded-full p-1 cursor-pointer transition-colors`}
          >
            <div
              className={`bg-white w-3 h-3 rounded-full shadow-md transform transition-transform ${
                showDetail ? "translate-x-4" : ""
              }`}
            ></div>
          </div>
          <KSpan text={lang.catalog.release_detail.show_detail} fontSize={15} />
        </div>
      )}
      {showDetail ? (
        <div>
          {releaseForm && <ReleaseLCView releaseForm={releaseForm} options={options} releaseId={params.releaseId} />}
        </div>
      ) : (
        <div>
          <div className="flex">
            <ReleaseComponent
              release={release}
              overrideBorder={true}
              fullScreen={true}
              canEdit={props.newReleasePermission}
              canCreateSmartLink={(props.user.userCredit?.smartLink || 0) > 0}
              isAdmin={isAdmin}
              updateReleaseEditStatus={props.updateReleaseEditStatus}
            />
          </div>
          <div className="">
            <div className="container pt-[60px] pb-[68px] !px-0">
              <ReleaseDetailTable
                title={lang.catalog.release_detail.title}
                columns={trackColumns}
                data={tableData}
                loading={loading}
                minRowCount={10}
                rowHeightInPx={40}
                topRightElement={<ReleaseDetailFilterComponents filterDataBySearchText={filterDataBySearchText} />}
                pagination
                limit={limit}
                count={paginatedTracks.count}
                onClickPage={onClickPage}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user,
    releases: state.catalogData.paginatedReleases.results,
    paginatedTracks: state.catalogData.paginatedReleaseTracks,
    userId: state.auth.user.id,
    release: state.catalogData.selectedRelease,
    shareData: state.catalogData.shareData,
    newReleasePermission: state.auth.user.permissionUser?.some((item) => {
      return item.permission && item.permission.id === PERMISSIONS.NewRelease
    })
  }
}

export default connect(mapStateToProps, { fetchReleaseTracks, selectRelease, updateReleaseEditStatus })(ReleaseDetail)
